/* eslint-disable */
import { Regions } from '@smh/projects/regions';
import { AxiosRequestConfig } from 'axios';

import { BaseHttpClient, catchError } from '@jtnews/shared/newsapi/base';
import type { BaseApiResponse } from '@jtnews/shared/newsapi/base';

type BookmarkDTO = {
  id: string;
  type: string;
  bookmark: { id: number };
};

export type ForumMenuItemDTO = {
  id: string;
  name: string;
  link: string;
  count?: number;
};

export type ForumProfileDataDTO = {
  id: number;
  passportId: number;
  login: string;
  email: string;
  link: string;
  avatar: string;
  sex: string;
  menu: ForumMenuItemDTO[];
  favourites: {
    data: ForumMenuItemDTO[];
    meta: {
      count: number;
    };
  } | null;
};

export type AuthUserInfoDTO = {
  avatar: string;
  commentFormProfileLink: string;
  email: string;
  hasMailAccount: boolean;
  hasMailsCounter: boolean;
  hasProfile: boolean;
  isBlocked: boolean;
  trusted: boolean;
  link: string;
  login: string;
  sex: 'female' | 'male' | 'unknown' | '';
  userId: number;
  responseTime: number;
  gameStat: {
    count: number;
    level: string;
  };
  bookmarks: BookmarkDTO[] | null;
  notifyAboutNewAnswers: boolean;
  notifyAboutNewVotes: boolean;
  notifyAboutCommentModeration: boolean;
  notifyEmail: string;
  isNotifyEmailValidated: boolean;
  featureFlags: Record<string, boolean> | null;
};

export type UserAuthParams = {
  username: string;
  password: string;
};

export class AuthApiService extends BaseHttpClient {
  private readonly _authUrl = '/public/account/';
  private readonly _authProfileNNUrl = '/jtnews/profile/';
  private readonly _sessionProfileNNUrl = '/jtnews/session/';
  private readonly _registerUrl = '/specs/users/register/';
  private readonly _confirmRegistrationUrl = '/specs/users/email/confirm/';
  // урлы для форумов NN
  private readonly _hostNN = `${this.protocol}://www.nn.ru`;
  private readonly _regionId: Regions;

  private get _host() {
    return this.getHost(this._regionId);
  }

  constructor() {
    super({
      envType: typeof window !== 'undefined' ? window.jtnConfig.envType : '',
      protocol: 'https'
    });

    this._regionId = typeof window !== 'undefined' ? window.jtnConfig.regionId : 54;
  }

  async checkAuthorization(): Promise<AuthUserInfoDTO | null> {
    const url = `${this._host}${this.v}${this._authUrl}`;
    const options: AxiosRequestConfig = {
      withCredentials: true,
      headers: {
        accept: 'vnd.news.v1.jtnews+json'
      },
      params: {
        regionId: this._regionId
      }
    };
    let response = null;

    try {
      response = await this.httpClient.get<AuthUserInfoDTO>(url, options);
    } catch (err) {
      return null;
    }

    return response.data;
  }

  @catchError
  async login(
    authParams: UserAuthParams,
    captchaToken: string
  ): Promise<AuthUserInfoDTO> {
    const url = `${this._host}${this.v}${this._authUrl}`;
    const data = {
      captchaToken
    };
    const options: AxiosRequestConfig = {
      withCredentials: true,
      headers: {
        accept: 'vnd.news.v1.jtnews+json'
      },
      auth: authParams,
      params: {
        regionId: this._regionId
      }
    };

    const response = await this.httpClient.post<AuthUserInfoDTO>(url, data, options);

    return response.data;
  }

  async checkAuthOnForumNN(): Promise<ForumProfileDataDTO | null> {
    const url = `${this._hostNN}${this._authProfileNNUrl}`;
    const options: AxiosRequestConfig = {
      withCredentials: true
    };
    let response = null;

    try {
      response = await this.httpClient.get<ForumProfileDataDTO>(url, options);
    } catch (err) {
      return null;
    }

    return response.data;
  }

  @catchError
  async loginForumsNN(username: string, password: string): Promise<ForumProfileDataDTO> {
    const url = `${this._hostNN}${this._authProfileNNUrl}`;
    const options: AxiosRequestConfig = {
      withCredentials: true,
      auth: {
        username,
        password
      }
    };
    const response = await this.httpClient.post<ForumProfileDataDTO>(url, null, options);
    return response.data;
  }

  @catchError
  async authThroughSocialsNN(): Promise<void> {
    const url = `${this._hostNN}${this._sessionProfileNNUrl}`;
    const options: AxiosRequestConfig = {
      withCredentials: true
    };

    await this.httpClient.post(url, null, options);
  }

  @catchError
  async register(email: string, password: string, captchaToken: string): Promise<string> {
    const url = `${this._host}${this.v}${this._registerUrl}`;
    const data = {
      email,
      password,
      captchaToken
    };
    const options = {
      withCredentials: true,
      headers: {
        accept: 'vnd.news.v1.jtnews+json'
      },
      params: {
        regionId: this._regionId
      }
    };

    const response = await this.httpClient.post<BaseApiResponse & { result: string }>(
      url,
      data,
      options
    );

    return response.data.result;
  }

  @catchError
  async sendConfirmRegistration(email: string, captchaToken: string): Promise<string> {
    const url = `${this._host}${this.v}${this._confirmRegistrationUrl}`;
    const data = {
      email,
      captchaToken
    };
    const options = {
      withCredentials: true,
      headers: {
        accept: 'vnd.news.v1.jtnews+json'
      },
      params: {
        regionId: this._regionId
      }
    };

    const response = await this.httpClient.post<BaseApiResponse & { result: string }>(
      url,
      data,
      options
    );

    return response.data.result;
  }
}

export const authApiService = new AuthApiService();
